/**
 * InteractiveArtistCard — fetches one artist (by slug) plus their upcoming
 * shows and Hoard tracks, then renders the same ArtistCard used on
 * /featured-artists. Used inside the /for-bands tutorial so prospective
 * musicians can click and hear what a real Hoard Artist node feels like.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { ArtistCard } from "../pages/FeaturedArtistsPage";
import { API } from "../App";

const InteractiveArtistCard = ({ slug }) => {
  const [artist, setArtist] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/featured-artists`).then(r => r.data).catch(() => []),
      Promise.all(["live-music", "open-mic", "karaoke"].map(c =>
        axios.get(`${API}/events?category=${c}`).then(r => r.data).catch(() => [])
      )).then(arrs => arrs.flat()),
      axios.get(`${API}/trove?item_type=music`).then(r => r.data).catch(() => []),
    ]).then(([allArtists, ev, mu]) => {
      if (cancelled) return;
      const target = (allArtists || []).find(a => a.slug === slug) || null;
      const now = Date.now();
      const shows = (ev || [])
        .filter(e => e?.host_slug === slug)
        .filter(e => new Date(e.start_datetime).getTime() >= now - 60 * 60 * 1000)
        .sort((x, y) => (x.start_datetime || "").localeCompare(y.start_datetime || ""));
      setArtist(target);
      setUpcoming(shows);
      setTracks(Array.isArray(mu) ? mu : []);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div
        className="bg-white rounded-2xl border border-[#E2E0DC] p-6 text-center text-sm text-[#5C5C5C]"
        data-testid="interactive-artist-card-loading"
      >
        Loading live artist node…
      </div>
    );
  }
  if (!artist) {
    return (
      <div
        className="bg-white rounded-2xl border border-[#E2E0DC] p-6 text-center text-sm text-[#5C5C5C]"
        data-testid="interactive-artist-card-empty"
      >
        Couldn’t load this artist preview right now.
      </div>
    );
  }
  return (
    <div data-testid={`interactive-artist-card-${slug}`}>
      <ArtistCard artist={artist} upcoming={upcoming} tracks={tracks} />
    </div>
  );
};

export default InteractiveArtistCard;
